import PropTypes from 'prop-types';
import React, {useCallback, useRef} from 'react';
import {getSubmitButtonText} from '../../../utils/getSubmitButtonText';
import {handleEnterKey} from '../../../utils/helpers';
import Spinner from '../../atoms/Spinner';
import ExpiryDate from '../../molecules/ExpiryDate';
import SubmitButton from '../../molecules/SubmitButton';
import ZepzCardNumberField from '../../molecules/ZepzCardNumber';
import ZepzCvvField from '../../molecules/ZepzCvvField';
import ZepzFieldWrapper from '../../molecules/ZepzFieldWrapper';
import ZepzPayinPropTypes from '../../pages/ZepzPayin/zepz-payin.proptypes';
import {TOKENEX_CARD_TYPES_MAPPING} from './consts';
import {FIELDS} from './zepz-form.fields';
import {
    useZepzForm, useZepzIframe, useZepzInitData
} from './zepz-form.hooks';

const ZepzForm = ({
    initData,
    translations,
    calculation,
    languageCode,
    paymentSessionId,
    dispatch,
    csrfToken,
    platform,
    genericError
}) => {
    const accountNumberContainer = useRef();
    const cvvContainer = useRef();
    const threeDSContainer = useRef();

    const threeDSData = {
        container: threeDSContainer,
        languageCode,
        paymentSessionId
    };

    const {data} = useZepzInitData({
        initData,
        languageCode,
        paymentSessionId,
        dispatch
    });

    const {iframe} = useZepzIframe({
        container: accountNumberContainer,
        cvvContainer,
        translations,
        initData: data
    });
    const {
        onChange,
        onBlur,
        onSubmit,
        state: {
            values, touched, errors, isValid, isSubmitting, possibleCardType
        }
    } = useZepzForm({
        iframe, csrfToken, platform, threeDSData, dispatch
    });

    const {
        zepz: {
            labels,
            errors: errorsTranslations
        }
    } = translations;

    const getErrorMessage = name => (errors[name] ? errorsTranslations[name][errors[name]] : '');
    const getCardNumberErrorMessage = () => getErrorMessage(FIELDS.cardNumber.name).replace(
        '{{possibleCardType}}',
        TOKENEX_CARD_TYPES_MAPPING[possibleCardType]
    );

    const onExpirationDateBlur = useCallback(() => onBlur(FIELDS.expiryDate.name), [onBlur]);
    const onExpirationDateChange = useCallback(value => onChange(FIELDS.expiryDate.name, value), [onChange]);
    const onSliderChange = useCallback(event => {
        onBlur(FIELDS.shouldSaveCard.name);
        onChange(FIELDS.shouldSaveCard.name, event.target.checked);
    }, [onChange, onBlur]);


    return (
        <form className="zepz-form" onSubmit={onSubmit}>
            {!iframe && <Spinner />}
            <div className="flex-row">
                <div className="flex-column">
                    <ZepzFieldWrapper
                        name={FIELDS.cardNumber.name}
                        label={labels.cardNumber}
                        isTouched={touched[FIELDS.cardNumber.name]}
                        error={getCardNumberErrorMessage()}
                    >
                        <ZepzCardNumberField
                            id={FIELDS.cardNumber.id}
                            name={FIELDS.cardNumber.name}
                            forwardRef={accountNumberContainer}
                            iframe={iframe}
                            cardType={possibleCardType}
                        />
                    </ZepzFieldWrapper>
                </div>
            </div>
            <div className="flex-row">
                <div className="flex-column flex-column-2">
                    <ZepzFieldWrapper
                        name={FIELDS.expiryDate.name}
                        label={labels.expiryDate}
                        isTouched={touched[FIELDS.expiryDate.name]}
                        error={getErrorMessage(FIELDS.expiryDate.name)}
                    >
                        <ExpiryDate
                            id={FIELDS.expiryDate.id}
                            name={FIELDS.expiryDate.name}
                            onChange={onExpirationDateChange}
                            onBlur={onExpirationDateBlur}
                        />
                    </ZepzFieldWrapper>
                </div>
                <div className="flex-column flex-column-2">
                    <ZepzFieldWrapper
                        name={FIELDS.securityCode.name}
                        label={labels.securityCode}
                        isTouched={touched[FIELDS.securityCode.name]}
                        error={getErrorMessage(FIELDS.securityCode.name)}
                    >
                        <ZepzCvvField
                            id={FIELDS.securityCode.id}
                            name={FIELDS.securityCode.name}
                            iframe={iframe}
                            forwardRef={cvvContainer}
                            translations={translations}
                        />
                    </ZepzFieldWrapper>
                </div>
            </div>

            <div className="container">
                <div className="switch">
                    <input
                        type="checkbox"
                        id={FIELDS.shouldSaveCard.id}
                        name={FIELDS.shouldSaveCard.name}
                        onChange={onSliderChange}
                        checked={values[FIELDS.shouldSaveCard.name]}
                        onKeyPress={handleEnterKey(onSliderChange)}
                        key={values[FIELDS.shouldSaveCard.name]}
                    />
                    <label className="slider round" htmlFor={FIELDS.shouldSaveCard.id}>{}</label>
                </div>
                <label className="switch-label" htmlFor={FIELDS.shouldSaveCard.id}>
                    {translations.zepz.saveCardForFuture}
                </label>
            </div>

            <SubmitButton disabled={genericError || !isValid} isSubmitting={isSubmitting}>
                <span>{getSubmitButtonText({
                    calculation,
                    prefix: translations.zepz.submitButton
                })}
                </span>
            </SubmitButton>
            <div id="three-ds-container" ref={threeDSContainer} />
        </form>
    );
};

ZepzForm.propTypes = {
    initData: ZepzPayinPropTypes.FormData,
    translations: ZepzPayinPropTypes.Translations.isRequired,
    calculation: ZepzPayinPropTypes.Calculations,
    languageCode: PropTypes.string.isRequired,
    paymentSessionId: PropTypes.string.isRequired,
    dispatch: PropTypes.func.isRequired,
    csrfToken: PropTypes.string.isRequired,
    genericError: PropTypes.bool
};

ZepzForm.defaultProps = {
    initData: null,
    calculation: null,
    genericError: false
};

export default ZepzForm;
